import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import Badge from "@/v2-backoffice-client/components/badge"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import Manage from "@/v2-backoffice-client/components/manage"
import LicenseSeatBadge from "@/v2-backoffice-client/license/LicenseSeatBadge"
import LicenseSeatCountField from "@/v2-backoffice-client/license/seats/LicenseSeatCountField"
import LicenseSeatHolderRow from "@/v2-backoffice-client/license/seats/LicenseSeatHolderRow"
import LicenseSeatLimitForm from "@/v2-backoffice-client/license/seats/LicenseSeatLimitForm"
import LicenseSeatPolicySwitch from "@/v2-backoffice-client/license/seats/LicenseSeatPolicySwitch"
import { useLicenseSeatUsersQuery } from "@/v2-backoffice-client/license/seats/license.seats.query"
import { useLicenseSeatLimitRemoveMutation } from "@/v2-backoffice-client/license/seats/license.seats.limitRemove.mutation"
import { evictLicenseSeatQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseSeatHoldersMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  license: LicenseFragment
}

// Seats on a seated license (a product whose seats can be limited to named
// people — the MCP servers). The license is in one of two modes, and the
// dialog shows the one it is in (FUS-2766):
//
//   * Everyone: every active user of the customer has what the license
//     carries. The dialog offers the one step out: pick the people and the
//     count they pay for, saved together (LicenseSeatLimitForm).
//   * Limited: only the named people have it. The dialog shows the count,
//     the people with a switch each (one more name, one fewer; the last
//     name cannot be switched off), who may change the names, and the one
//     step back: Remove limit.
//
// Over the count is allowed and shown, never refused — that is a billing
// conversation, not a lockout. A stale name (someone who left) is listed to
// be revoked; it does not change the mode.
function LicenseSeatHoldersMenuItem(props: LicenseSeatHoldersMenuItemProps) {
  const { license, children, ...menuItemProps } = props
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <ActionMenu.Item
        iconSrc="user-check"
        data-feature-code="UI_BackofficeLicense_Row_SeatHolders"
        onSelect={(e) => {
          e.preventDefault()
          setIsOpen(true)
        }}
        {...menuItemProps}
      >
        {children ?? "Seats"}
      </ActionMenu.Item>

      {/* Mounted per open so the form re-seeds from the license every time. */}
      {isOpen && (
        <LicenseSeatsDialog
          license={license}
          open={isOpen}
          onOpenChange={setIsOpen}
        />
      )}
    </>
  )
}

type LicenseSeatsDialogProps = {
  license: LicenseFragment
  open: boolean
  onOpenChange(open: boolean): void
}

function LicenseSeatsDialog(props: LicenseSeatsDialogProps) {
  const { license, open, onOpenChange } = props

  const { data, loading, error } = useLicenseSeatUsersQuery({
    variables: { licenseId: license.Id },
  })
  const people = data?.getLicenseSeatUsers ?? []

  const [removeLimit, { loading: isRemoving }] =
    useLicenseSeatLimitRemoveMutation({
      update: evictLicenseSeatQueries,
      onCompleted(data) {
        toast({
          variant: "success",
          title: "Seat limit removed",
          description: `Everyone at ${data.removeLicenseSeatLimit.CustomerName ?? "the customer"} has ${data.removeLicenseSeatLimit.ProductName} again.`,
        })
      },
      onError(error) {
        toast({
          variant: "destructive",
          title: "Seat limit not removed",
          description: error.message,
        })
      },
    })

  const customerName = license.CustomerName ?? "the customer"
  const named = license.NamedSeats
  const hasCount = license.Seats != null
  const seatsLabel = `${license.Seats} ${license.Seats === 1 ? "seat" : "seats"}`

  const stateDescription = license.SeatsRestricted
    ? hasCount
      ? `Only the people named below have this license. ${customerName} pays for ${seatsLabel}; naming more is allowed and shows as over, for invoicing.`
      : `Only the people named below have this license. There is no seat count on it yet.`
    : `Everyone at ${customerName} has this license. To limit it to named people, tick them below and save.`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Capped to the viewport with the people list scrolling inside: a
          customer with 76 users would otherwise push the lower rows past
          the body, where no scroll reaches them. */}
      <Dialog.Content
        align="top"
        className="flex max-h-[85vh] flex-col overflow-hidden"
      >
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2">
            Seats — {license.ProductName}
            {license.SeatsRestricted ? (
              hasCount ? (
                <LicenseSeatBadge
                  occupied={license.SeatsAssigned}
                  cap={license.Seats}
                  tooltip="Named people against the licensed seats"
                />
              ) : (
                <Badge
                  variant="positive"
                  tooltip="No seat count on this license"
                >
                  {named} named
                </Badge>
              )
            ) : (
              <Badge
                variant="neutral"
                tooltip="Not limited: every active user of the customer has it"
              >
                Everyone
              </Badge>
            )}
          </Dialog.Title>
          <Dialog.Description>{stateDescription}</Dialog.Description>
        </Dialog.Header>

        {license.SeatsRestricted ? (
          <>
            <ErrorMessage className="my-2 text-center" error={error} />
            <LicenseSeatCountField
              key={license.Seats ?? "none"}
              license={license}
            />
            <LicenseSeatPolicySwitch license={license} />

            {loading ? (
              <Manage.Skeleton count={5} aria-busy="true" />
            ) : people.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No active users at this customer yet.
              </p>
            ) : (
              <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto pr-1">
                {people.map((person) => (
                  <LicenseSeatHolderRow
                    key={person.UserId}
                    licenseId={license.Id}
                    person={person}
                  />
                ))}
              </ul>
            )}

            <Dialog.Footer className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3">
              <p className="text-xs text-muted-foreground">
                The last named person cannot be switched off; remove the limit
                instead.
              </p>
              <ConfirmButton
                variant="outline"
                iconSrc="users"
                content="Back to everyone at the customer. The names are dropped, and so is an operator license's seat count."
                confirmContent={`Remove the limit? Everyone at ${customerName} gets ${license.ProductName} again.`}
                confirmVariant="destructive"
                isLoading={isRemoving}
                data-feature-code="UI_BackofficeLicense_Seats_RemoveLimit"
                onConfirm={() => {
                  void removeLimit({ variables: { licenseId: license.Id } })
                }}
              >
                Remove Limit
              </ConfirmButton>
            </Dialog.Footer>
          </>
        ) : (
          <LicenseSeatLimitForm
            license={license}
            people={people.filter((person) => person.IsActiveUser)}
            isLoading={loading}
            error={error}
            onLimited={() => {
              onOpenChange(false)
            }}
          />
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseSeatHoldersMenuItem
